import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { ScreenToShowState } from '../types/ScreenToShowState';

// Keep club registration screen navigation in browser history and in sync with url params
export function useClubRegistrationHistory(
  screenToShow: ScreenToShowState,
  setScreenToShow: (screen: ScreenToShowState) => void,
  enableHistory: boolean,
) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const updateURL = (step: ScreenToShowState) => {
    if (!enableHistory) { return; }

    const urlParams = new URLSearchParams(window.location.search);
    if (step !== ScreenToShowState.INITIAL) {
      urlParams.set('clubRegistrationStep', step);
    } else {
      urlParams.delete('clubRegistrationStep');
    }

    const currentHash = window.location.hash || '';
    router.push(`?${urlParams.toString()}${currentHash}`, { scroll: false });
  };

  // Update the screen based on URL params
  useEffect(() => {
    if (!enableHistory) { return; }

    const clubRegistrationStepParam = searchParams.get('clubRegistrationStep');
    if (clubRegistrationStepParam) {
      setScreenToShow(clubRegistrationStepParam as ScreenToShowState);
    } else {
      setScreenToShow(ScreenToShowState.INITIAL);
    }
  }, [searchParams, enableHistory]);

  // Update the URL when screenToShow changes
  useEffect(() => {
    if (!enableHistory) { return; }
    updateURL(screenToShow);
  }, [screenToShow, enableHistory]);

  // Remove the URL parameter when the component unmounts
  useEffect(() => () => {
    if (!enableHistory) { return; }

    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete('clubRegistrationStep');

    const currentHash = window.location.hash || '';
    router.replace(`?${urlParams.toString()}${currentHash}`, { scroll: false });
  }, [enableHistory]);
}
